import {
  CircleNotch,
  File as FileIcon,
  FileDoc,
  FilePdf,
  FileText,
  Trash,
  Upload,
  WarningCircle,
} from "@phosphor-icons/react";
import { Badge, Button, Flex, Text, Tooltip } from "@radix-ui/themes";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  type SkillDocument,
  deleteSkillDocument,
  getSkillDocumentPresignedUrl,
  registerSkillDocument,
  uploadFileToS3,
} from "../../../services/skill-documents";
import { useFileDrop } from "../hooks/useFileDrop";

const DOCUMENT_LIMIT = 10;
const DOCUMENT_AMBER_THRESHOLD = 8;

const ACCEPTED_SKILL_DOC_TYPES = [
  ".pdf",
  ".txt",
  ".docx",
  ".png",
  ".jpg",
  ".jpeg",
];

export interface SkillDocumentsPanelProps {
  skillId: string;
  skillType: "builtin" | "custom";
  documents: SkillDocument[];
  onDocumentsChange: (docs: SkillDocument[]) => void;
  disabled?: boolean;
}

type UploadState =
  | { status: "uploading"; fileName: string; fileId: string }
  | { status: "error"; fileName: string; fileId: string; file: File };

function getFileIcon(name: string) {
  const ext = name.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "pdf":
      return (
        <FilePdf size={16} className="shrink-0 text-puntt-neutral-gray-9" />
      );
    case "doc":
    case "docx":
      return (
        <FileDoc size={16} className="shrink-0 text-puntt-neutral-gray-9" />
      );
    case "txt":
      return (
        <FileText size={16} className="shrink-0 text-puntt-neutral-gray-9" />
      );
    default:
      return (
        <FileIcon size={16} className="shrink-0 text-puntt-neutral-gray-9" />
      );
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function getMimeLabel(mimeType: string): string {
  const map: Record<string, string> = {
    "application/pdf": "PDF",
    "text/plain": "TXT",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "DOCX",
    "image/png": "PNG",
    "image/jpeg": "JPG",
  };
  return map[mimeType] ?? mimeType.split("/")[1]?.toUpperCase() ?? "FILE";
}

function generateFileId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const SkillDocumentsPanel = ({
  skillId,
  skillType,
  documents,
  onDocumentsChange,
  disabled = false,
}: SkillDocumentsPanelProps) => {
  const [uploadStates, setUploadStates] = useState<UploadState[]>([]);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isAtLimit = documents.length >= DOCUMENT_LIMIT;
  const uploadingCount = uploadStates.filter(
    (s) => s.status === "uploading",
  ).length;
  const totalCount = documents.length + uploadingCount;

  const capacityColor =
    totalCount >= DOCUMENT_LIMIT
      ? "text-red-600"
      : totalCount >= DOCUMENT_AMBER_THRESHOLD
        ? "text-orange-500"
        : "text-puntt-neutral-gray-9";

  // Track the latest documents synchronously so concurrent uploads each see the
  // most recent registrations from sibling uploads (the `documents` prop only
  // refreshes between renders, so two uploads completing in the same tick would
  // otherwise both append to the same stale snapshot and lose the other).
  const documentsRef = useRef(documents);
  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  const doUpload = useCallback(
    async (file: File, fileId: string) => {
      try {
        const idField =
          skillType === "custom" ? { workflowId: skillId } : { skillId };
        const presigned = await getSkillDocumentPresignedUrl({
          ...idField,
          name: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
        });

        await uploadFileToS3(presigned.url, presigned.fields, file);

        const registered = await registerSkillDocument({
          ...idField,
          documentId: presigned.documentId,
          name: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
        });

        const next = [...documentsRef.current, registered];
        documentsRef.current = next;
        onDocumentsChange(next);
        setUploadStates((prev) => prev.filter((s) => s.fileId !== fileId));
      } catch {
        setUploadStates((prev) =>
          prev.map((s) =>
            s.fileId === fileId
              ? ({
                  status: "error",
                  fileName: file.name,
                  fileId,
                  file,
                } as UploadState)
              : s,
          ),
        );
      }
    },
    [skillId, skillType, onDocumentsChange],
  );

  const handleFiles = useCallback(
    (files: File[]) => {
      if (disabled || isAtLimit) return;

      const remaining = DOCUMENT_LIMIT - documents.length - uploadingCount;
      const toUpload = files.slice(0, remaining);

      const newStates: UploadState[] = toUpload.map((f) => ({
        status: "uploading" as const,
        fileName: f.name,
        fileId: generateFileId(),
      }));
      setUploadStates((prev) => [...prev, ...newStates]);

      toUpload.forEach((file, i) => {
        const fileId = newStates[i].fileId;
        void doUpload(file, fileId);
      });
    },
    [disabled, isAtLimit, documents.length, uploadingCount, doUpload],
  );

  const handleRetry = useCallback(
    (errorState: Extract<UploadState, { status: "error" }>) => {
      setUploadStates((prev) =>
        prev.map((s) =>
          s.fileId === errorState.fileId
            ? ({
                status: "uploading",
                fileName: errorState.fileName,
                fileId: errorState.fileId,
              } as UploadState)
            : s,
        ),
      );
      void doUpload(errorState.file, errorState.fileId);
    },
    [doUpload],
  );

  const handleDelete = useCallback(
    async (doc: SkillDocument) => {
      setDeletingIds((prev) => new Set(prev).add(doc.documentId));
      try {
        const idField =
          skillType === "custom" ? { workflowId: skillId } : { skillId };

        await deleteSkillDocument({ ...idField, documentId: doc.documentId });
        const next = documentsRef.current.filter(
          (d) => d.documentId !== doc.documentId,
        );
        documentsRef.current = next;
        onDocumentsChange(next);
      } catch {
        // leave the document in the list on failure
      } finally {
        setDeletingIds((prev) => {
          const next = new Set(prev);
          next.delete(doc.documentId);
          return next;
        });
      }
    },
    [skillId, skillType, onDocumentsChange],
  );

  const { isDragOver, dropZoneProps } = useFileDrop({
    onDrop: handleFiles,
    accept: ACCEPTED_SKILL_DOC_TYPES,
  });

  const triggerUpload = () => {
    if (!disabled && !isAtLimit) {
      fileInputRef.current?.click();
    }
  };

  const uploadButton = (
    <Button
      variant="outline"
      size="2"
      onClick={triggerUpload}
      disabled={disabled || isAtLimit}
      className="cursor-pointer"
      data-testid="skill-docs-upload-btn"
    >
      <Upload size={14} />
      Upload document
    </Button>
  );

  return (
    <div className="flex flex-col gap-4" data-testid="skill-documents-panel">
      {/* Header row: capacity badge + upload trigger */}
      <Flex justify="between" align="center">
        <Text size="2" weight="medium" className="text-puntt-neutral-gray-12">
          Reference Documents
        </Text>
        <Flex align="center" gap="3">
          <span
            className={`text-xs font-medium ${capacityColor}`}
            data-testid="skill-docs-capacity"
          >
            {totalCount} / {DOCUMENT_LIMIT} documents
          </span>
          {isAtLimit ? (
            <Tooltip content="Document limit reached. Remove a document to upload a new one.">
              <span>{uploadButton}</span>
            </Tooltip>
          ) : (
            uploadButton
          )}
        </Flex>
      </Flex>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_SKILL_DOC_TYPES.join(",")}
        className="hidden"
        onChange={(e) => {
          if (e.target.files) {
            handleFiles(Array.from(e.target.files));
            e.target.value = "";
          }
        }}
        data-testid="skill-docs-file-input"
      />

      {/* Empty state + drop zone */}
      {documents.length === 0 && uploadStates.length === 0 ? (
        <button
          type="button"
          data-testid="skill-docs-empty-drop-zone"
          onClick={triggerUpload}
          disabled={disabled}
          className={`flex w-full flex-col items-center gap-3 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
            isDragOver
              ? "border-puntt-accent-7 bg-puntt-accent-2"
              : "border-puntt-neutral-gray-6 bg-puntt-neutral-gray-1 hover:border-puntt-accent-7 hover:bg-puntt-accent-2/30"
          } ${disabled ? "cursor-default opacity-50" : "cursor-pointer"}`}
          {...dropZoneProps}
        >
          <div className="flex size-12 items-center justify-center rounded-xl bg-puntt-neutral-gray-3">
            <Upload
              size={24}
              className={
                isDragOver ? "text-puntt-accent-9" : "text-puntt-neutral-gray-9"
              }
            />
          </div>
          <div className="flex flex-col gap-1">
            <Text
              size="3"
              weight="medium"
              className="text-puntt-neutral-gray-12"
            >
              No reference documents yet
            </Text>
            <Text size="2" className="max-w-sm text-puntt-neutral-gray-10">
              Upload brand guidelines, compliance criteria, or any reference
              material this skill will use during every review.
            </Text>
          </div>
          <Text size="1" className="text-puntt-neutral-gray-8">
            PDF, TXT, DOCX, PNG, JPG
          </Text>
        </button>
      ) : (
        /* Document list + drop zone */
        <div
          className={`rounded-xl border-2 border-dashed transition-colors ${
            isDragOver
              ? "border-puntt-accent-7 bg-puntt-accent-2/30"
              : "border-puntt-neutral-gray-6"
          }`}
          {...dropZoneProps}
        >
          <ul className="divide-y divide-puntt-neutral-gray-4">
            {documents.map((doc) => (
              <li
                key={doc.documentId}
                className="group flex items-center gap-3 px-4 py-3"
                data-testid={`skill-doc-item-${doc.documentId}`}
              >
                {getFileIcon(doc.name)}
                <div className="min-w-0 flex-1">
                  <Text
                    size="2"
                    weight="medium"
                    className="block truncate text-puntt-neutral-gray-12"
                  >
                    {doc.name}
                  </Text>
                  <Flex align="center" gap="2" className="mt-0.5">
                    <Badge size="1" variant="soft" color="gray">
                      {getMimeLabel(doc.mimeType)}
                    </Badge>
                    <Text size="1" className="text-puntt-neutral-gray-9">
                      {formatBytes(doc.sizeBytes)}
                    </Text>
                    <Text size="1" className="text-puntt-neutral-gray-8">
                      {formatDate(doc.uploadedAt)}
                    </Text>
                  </Flex>
                </div>
                <button
                  type="button"
                  data-testid={`skill-doc-delete-${doc.documentId}`}
                  onClick={() => handleDelete(doc)}
                  disabled={disabled || deletingIds.has(doc.documentId)}
                  className="hover:text-red-600 shrink-0 rounded p-1 text-puntt-neutral-gray-8 opacity-0 transition-opacity disabled:cursor-default disabled:opacity-50 group-hover:opacity-100"
                  aria-label={`Remove ${doc.name}`}
                >
                  {deletingIds.has(doc.documentId) ? (
                    <CircleNotch size={16} className="animate-spin" />
                  ) : (
                    <Trash size={16} />
                  )}
                </button>
              </li>
            ))}

            {/* In-progress uploads */}
            {uploadStates.map((state) => (
              <li
                key={state.fileId}
                className="flex items-center gap-3 px-4 py-3"
                data-testid={`skill-doc-upload-${state.fileId}`}
              >
                {state.status === "error" ? (
                  <WarningCircle size={16} className="text-red-500 shrink-0" />
                ) : (
                  <CircleNotch
                    size={16}
                    className="shrink-0 animate-spin text-puntt-accent-9"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <Text
                    size="2"
                    className="block truncate text-puntt-neutral-gray-11"
                  >
                    {state.fileName}
                  </Text>
                  {state.status === "error" && (
                    <Badge
                      size="1"
                      variant="soft"
                      color="red"
                      className="mt-0.5"
                    >
                      Upload failed
                    </Badge>
                  )}
                </div>
                {state.status === "error" && (
                  <button
                    type="button"
                    onClick={() =>
                      handleRetry(
                        state as Extract<UploadState, { status: "error" }>,
                      )
                    }
                    className="shrink-0 text-xs text-puntt-accent-9 hover:underline"
                    data-testid={`skill-doc-retry-${state.fileId}`}
                  >
                    Retry
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
